import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, ViewEncapsulation, OnChanges, SimpleChanges, ChangeDetectorRef, ContentChildren, QueryList, AfterContentInit, TemplateRef, ViewChild, ElementRef, Inject, Injector } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { ErrorStateMatcher, MatSidenavContent } from '@angular/material';
import { TemplatePortal } from '@angular/cdk/portal';
import { MediaMatcher } from '@angular/cdk/layout';
import { Observable, Subscription, Subject } from 'rxjs';
import { filter, switchMap, map } from 'rxjs/operators';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { AppConfig } from '@awp/core/config';
import { isNullOrEmpty, scrollToTarget } from '@awp/core/utils';
import { StepperIcon } from './stepper-icon';
import { StepperStateService, StateChanged } from './stepper-state.service';
import { StepIdentifier, Step, StepContext, StepItemContent, StepCompletedEventArgs, StepEventArgs } from './step';
import { ModalService } from '../../overlay/modal.service';

@Component({
  selector: 'ap-stepper',
  templateUrl: './stepper.component.html',
  host: {
    'class': 'ap-stepper',
  },
  providers: [{ provide: ErrorStateMatcher, useExisting: StepperComponent}],
  encapsulation: ViewEncapsulation.None
})
export class StepperComponent implements OnInit, OnDestroy, OnChanges, AfterContentInit, ErrorStateMatcher {

  activeStepInstance: Step | null;
  stepChanges: Observable<any>;
  stepChangesSubscription: Subscription;
  visibleSteps: StepItemContent[] = [];
  isMenuOpened = false;
  nextStep = new Subject<StepIdentifier>();
  regioncode = this.injector.get(AppConfig).REGION_CODE;
  countryname = this.injector.get(AppConfig).COUNTRY_NAME;
  _iconTemplates: {[key: string]: TemplateRef<any>} = {};

  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  @Input() steps: StepItemContent[];
  @Input() activeStep: StepItemContent;
  @Input() stepperVisible = true;
  @Input() stepNextLabel = 'Next';
  @Input() stepHeaderLabel: string | TemplateRef<any>;
  @Input() loading = false;
  @Input() context: StepContext;

  @Output() stepCompleted = new EventEmitter<StepCompletedEventArgs>();
  @Output() stepChanged = new EventEmitter<StepItemContent>();
  @Output() stepActivated = new EventEmitter<StepEventArgs>();
  @Output() stepDeactivated = new EventEmitter<StepEventArgs>();
  @Output() menuOpened = new EventEmitter<boolean>();

  @ContentChildren(StepperIcon) _icons: QueryList<StepperIcon>;

  @ViewChild('sidenavContent', { read: ElementRef, static: true }) sidenavContentRef: ElementRef;
  @ViewChild(MatSidenavContent, { static: true }) sideNavContent: MatSidenavContent;
  @ViewChild('loadingModal', { static: true }) loadingModal: TemplatePortal<any>;

  constructor(
    private modalService: ModalService,
    public stepperState: StepperStateService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    private injector: Injector,
    private media: MediaMatcher) {

    this._mobileQueryListener = () => {
      this.changeDetectorRef.detectChanges();
      this.closeMenu();
     };
    this.mobileQuery = this.media.matchMedia(`(max-width: 767px)`);
    this.mobileQuery.addListener(this._mobileQueryListener);

    this.stepperState.stateChanges
      .pipe(untilDestroyed(this))
      .subscribe(p => {
        if (p.state === StateChanged.VisibilityChange) {
          this.updateVisibleSteps();
        }

        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnInit(): void {
    this.updateVisibleSteps();

    this.nextStep
      .pipe(
        switchMap(nextStep => {
          return this.activeStepInstance.isStepComplete()
                  .pipe(map(success => success ? nextStep : null));
        }),
        filter(p => p != null),
        untilDestroyed(this)
      )
      .subscribe(nextStep => {
        this.stepCompleted.emit({
          step: this.activeStep,
          componentInstance: this.activeStepInstance,
          nextStep
        });
      });
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeStep != null && changes.activeStep.currentValue != null) {
      this.stepperState.markStepAsVisited(changes.activeStep.currentValue.id);
    }
    if (changes.loading != null) {
      this.showLoadingIndicator();
    }
    if (changes.context && this.activeStepInstance != null) {
      this.activeStepInstance.context = changes.context.currentValue;
    }
  }

  ngAfterContentInit(): void {
    this._icons.forEach(({name, templateRef}) => {
      this._iconTemplates[name] = templateRef;
    });

    // Recalculate margin changes for sidebarnav
    this.sideNavContent._container._contentMarginChanges.next();
  }

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const errorState = !!(control && control.invalid
        && (control.touched || this.stepperState.hasSubmitted(this.activeStep.id)));

    return errorState;
  }

  nextStepCallback(): void {
    this.onStepNextClick(this.activeStep);
  }

  private updateVisibleSteps(): void {
    if (this.steps == null || !this.stepperState.hasVisibleSteps()) {
      this.visibleSteps = [];
    } else {
      this.visibleSteps = this.steps.filter(p => this.stepperState.isStepVisible(p.id) && isNullOrEmpty(p.parentId));
    }
  }

  onStepNextClick(step: StepItemContent): void {
    if (this.activeStepInstance != null) {
      this.activeStepInstance.saveChanges({ next: true });
    }
    this.stepperState.markStepAsSubmitted(step.id);

    if (this.activeStepInstance.isValid) {
      const nextStep = this.activeStepInstance.getNextStep() || this.getNextStep();
      this.nextStep.next(nextStep);
    } else {
      this.scrollToError(true);
    }
  }

  gotoStep(step: StepItemContent): void {
    if (this.activeStepInstance != null) {
      this.activeStepInstance.saveChanges(); // Persist any changes to the form.
    }

    // Assume parent step navigation, need to navigate to first child
    if (isNullOrEmpty(step.parentId) && isNullOrEmpty(step.stepComponentName)) {
      const childSteps = this.steps.filter(p => p.parentId === step.id);
      if (childSteps.length > 0) {
        step = childSteps.find(p => this.stepperState.isComplete(p.id));
      }
    }

    this.stepChanged.emit(step);
  }

  gotoPreviousStep(): void {
    this.gotoStep(this.getPreviousStep());
  }

  getNextStep(): StepItemContent | null {
    if (this.activeStep == null) {
      return this.steps != null && this.steps.length > 0 ? this.steps[0] : null;
    }

    const pageIndex = this.steps.findIndex(p => p.id === this.activeStep.id);

    if (pageIndex >= 0 && pageIndex < this.visibleSteps.length) {
      return this.visibleSteps[pageIndex + 1];
    }

    return null;
  }

  getPreviousStep(): StepItemContent | null {
    if (this.activeStep == null) {
      return this.steps != null && this.steps.length > 0 ? this.steps[0] : null;
    }

    const pageIndex = this.steps.findIndex(p => p.id === this.activeStep.id);

    if (pageIndex > 0) {
      return this.steps[pageIndex - 1];
    }

    return null;
  }

  onActivateStep(step: StepItemContent, componentInstance: Step): void {
    if (componentInstance == null || componentInstance.changes == null) return;

    this.activeStepInstance = componentInstance;
    this.activeStepInstance.step = step;
    this.activeStepInstance.context = this.context;
    this.activeStepInstance.registerNextStep(this.nextStepCallback.bind(this));

    this.stepChangesSubscription = this.activeStepInstance.changes
      .pipe(untilDestroyed(this))
      .subscribe(valid => {
        valid
          ? this.stepperState.enableNext()
          : this.stepperState.disableNext();
      });

    componentInstance.activateStep();
    this.stepActivated.emit({ step, componentInstance });
    this.stepperState.changeActiveStep(step.id);
    this.scrollContentToTop();
  }

  onDeactivateStep(step: StepItemContent, componentInstance: Step): void {
    if (componentInstance != null) {
      componentInstance.deactivateStep();
    }

    if (this.stepChangesSubscription != null) {
      this.stepChangesSubscription.unsubscribe();
    }

    this.activeStepInstance = null;
    this.stepperState.disableNext(); // Not sure if I should be doing this here??
    this.stepDeactivated.emit({ step, componentInstance });
  }

  onToggleMenu(): void {
    if (!this.mobileQuery.matches) return;

    const opened = !this.isMenuOpened;
    this.menuOpened.emit(opened);
    setTimeout(() => {
      this.isMenuOpened = opened;
      this.changeDetectorRef.markForCheck();
    }, 0);
  }

  closeMenu(): void {
    if(this.isMenuOpened){
      this.onToggleMenu();
    }
  }

  getTemplateIcon(stepItem: StepItemContent): TemplateRef<any> {
    // Todo: This isn't performant by necessary

    if (stepItem != null && this.activeStep != null) {
      const state = this.stepperState.getStepState(stepItem.id, this.activeStep.id, this.activeStep.parentId);
      return this._iconTemplates[state];
    }

    return this._iconTemplates['none'];
  }

  stepTrackBy(index: number, item: StepItemContent): string {
    if (item == null) return null;
    return item.id;
  }

  isTemplateHeader(): boolean {
    return typeof this.stepHeaderLabel !== 'string';
  }

  private scrollContentToTop() : void {
    scrollToTarget(this.sidenavContentRef.nativeElement, 0);
  }

  private scrollToError(retry: boolean) {
    const matErrorElements = Array.from(this.document.getElementsByTagName('mat-error'));
    if (matErrorElements.length > 0) {
      matErrorElements[0].scrollIntoView({ behavior: 'smooth'});
    } else if (retry) {
      setTimeout(() => this.scrollToError(false), 500);
    }
  }

  showLoadingIndicator(): void {
    if (this.loading) {
      this.modalService.open(this.loadingModal);
    } else {
      this.modalService.close();
    }
  }
}
